import React, { useState, useEffect } from 'react';
import T from '../../components/common/T';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useInventory } from '../../context/InventoryContext';
// import Layout from "../../components/Layout";

// 🟢 SECURITY IMPORT
import { useAuth } from "../../context/AuthContext";
import { toast } from 'react-toastify';

const WarehouseDetailView = () => {
    const { warehouseId } = useParams();
    const navigate = useNavigate();
    const { getWarehouse, getInventoryByWarehouse, stockHistory } = useInventory();
    
    // 🟢 GET CHECKER
    const { checkAccess } = useAuth();

    // 🟢 PAGE GUARD
    useEffect(() => {
        if (!checkAccess('warehouse.view')) {
            toast.error("Access Denied.");
            navigate('/admin/warehouse-management');
        }
    }, [checkAccess, navigate]);

    const [activeTab, setActiveTab] = useState('inventory'); 

    const warehouse = getWarehouse(warehouseId);
    const warehouseInventory = getInventoryByWarehouse(warehouseId);

    // FILTER HISTORY: Only records for this warehouse
    const warehouseHistory = stockHistory
        .filter(record => String(record.warehouseId) === String(warehouseId))
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)); 

    if (!checkAccess('warehouse.view')) return null;
    if (!warehouse) {
        return <><div className="p-6 text-red-500 font-bold"><T>Warehouse not found.</T></div></>;
    }

    return (
        <>
            <div className="p-6 bg-gray-50 dark:bg-[#0d1117] min-h-screen">
                <div className="mb-6">
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-xl font-bold text-gray-900 dark:text-white">{warehouse.name}</h1>
                            <p className="text-gray-500 dark:text-slate-400 mt-1">ID: {warehouse.id} • Total Bins: {warehouse.bins?.length || 0}</p>
                        </div>
                        <Link to="/admin/warehouse-management" className="text-blue-600 dark:text-blue-400 hover:underline"><T>Back to List</T></Link>
                    </div>
                </div>

                {/* TABS */}
                <div className="flex gap-6 border-b border-gray-200 dark:border-[#30363d] mb-6">
                    <button 
                        onClick={() => setActiveTab('inventory')}
                        className={`pb-2 px-1 text-sm font-medium transition ${activeTab === 'inventory' ? 'border-b-2 border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white'}`}
                    >
                        Current Stock
                    </button>
                    <button 
                        onClick={() => setActiveTab('history')}
                        className={`pb-2 px-1 text-sm font-medium transition ${activeTab === 'history' ? 'border-b-2 border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white'}`}
                    >
                        Movement History
                    </button>
                </div>

                {/* TAB CONTENT */}
                {activeTab === 'inventory' ? (
                    <div className="bg-white dark:bg-[#161b22] rounded-lg shadow-xs border border-gray-200 dark:border-[#30363d] overflow-hidden">
                        {warehouseInventory.length === 0 ? (
                            <div className="p-8 text-center text-gray-400 dark:text-slate-500"><T>No stock currently held in this warehouse.</T></div>
                        ) : (
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-50 dark:bg-[#21262d] text-gray-500 dark:text-slate-400 font-bold border-b border-gray-200 dark:border-[#30363d]">
                                    <tr>
                                        <th className="px-6 py-3"><T>Item</T></th>
                                        <th className="px-6 py-3"><T>SKU</T></th>
                                        <th className="px-6 py-3"><T>Bin Location</T></th>
                                        <th className="px-6 py-3 text-right"><T>Quantity</T></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-[#30363d]">
                                    {warehouseInventory.map((item, idx) => {
                                        // 🟢 FIX: Find the specific location data for THIS warehouse
                                        const locationData = item.locations?.find(loc => String(loc.warehouseId) === String(warehouseId));
                                        
                                        return (
                                            <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-slate-800/50 transition">
                                                <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">{item.itemName}</td>
                                                <td className="px-6 py-4 text-gray-500 dark:text-slate-400">{item.sku}</td>
                                                <td className="px-6 py-4">
                                                    <span className="bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 px-2 py-1 rounded text-xs font-mono border border-gray-200 dark:border-slate-700">
                                                        {locationData ? locationData.bin : '-'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-right font-bold text-gray-800 dark:text-white">
                                                    {locationData ? locationData.qty : 0}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                ) : (
                    <div className="bg-white dark:bg-[#161b22] rounded-lg shadow-xs border border-gray-200 dark:border-[#30363d] overflow-hidden">
                        {warehouseHistory.length === 0 ? (
                            <div className="p-8 text-center text-gray-400 dark:text-slate-500"><T>No movement history found.</T></div>
                        ) : (
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-50 dark:bg-[#21262d] text-gray-500 dark:text-slate-400 font-bold border-b border-gray-200 dark:border-[#30363d]">
                                    <tr>
                                        <th className="px-6 py-3"><T>Date</T></th>
                                        <th className="px-6 py-3"><T>Type</T></th>
                                        <th className="px-6 py-3"><T>Item</T></th>
                                        <th className="px-6 py-3"><T>Reference / details</T></th>
                                        <th className="px-6 py-3"><T>Project</T></th>
                                        <th className="px-6 py-3 text-right"><T>Qty Change</T></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-[#30363d]">
                                    {warehouseHistory.map((record, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-slate-800/50 transition">
                                            <td className="px-6 py-4 text-gray-500 dark:text-slate-400 whitespace-nowrap">
                                                {new Date(record.timestamp).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-1 rounded text-xs font-bold ${
                                                    record.type === 'IN' ? 'bg-green-100 dark:bg-emerald-950/50 text-green-700 dark:text-emerald-400 border border-green-200 dark:border-emerald-800/60' : 'bg-red-100 dark:bg-rose-950/50 text-red-700 dark:text-rose-400 border border-red-200 dark:border-rose-800/60'
                                                }`}>
                                                    {record.type}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">{record.itemName}</td>
                                            
                                            {/* SHOW REFERENCE */}
                                            <td className="px-6 py-4 text-xs text-gray-600 dark:text-slate-300 font-mono">
                                                {record.note || '-'}
                                                <div className="text-gray-400 dark:text-slate-500 text-[10px]">
                                                    {record.type === 'IN' ? `From: ${record.from}` : `To: ${record.to}`}
                                                </div>
                                            </td>

                                            <td className="px-6 py-4 text-sm text-blue-600 dark:text-blue-400 font-medium">
                                                {record.projectName || '-'}
                                            </td>
                                            <td className={`px-6 py-4 text-sm font-bold text-right ${record.type === 'IN' ? 'text-green-600 dark:text-emerald-400' : 'text-red-600 dark:text-rose-400'}`}>
                                                {record.type === 'IN' ? '+' : '-'}{record.quantity}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default WarehouseDetailView;